import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { useGetUserProfileQuery } from '../store/api/userApi';
import { useGetBalanceQuery, useGetMyTransactionsQuery } from '../store/api/balanceApi';
import { useGetProxiesQuery } from '../store/api/proxiesApi';
import { useGetPermanentNumbersQuery } from '../store/api/permanentNumbersApi';
import { useGetTemporaryNumbersQuery } from '../store/api/temporaryNumbersApi';
import { formatDate } from '../utils/dateFormatter';
import { useLanguage } from '../hooks/useLanguage';
import { TELEGRAM_BOT_URL } from '../utils/constants';

function MiniSpark({ light = false }: { light?: boolean }) {
  const stroke = light ? '#fff' : '#3b82f6';
  const fill = light ? 'rgba(255,255,255,0.2)' : 'rgba(59,130,246,0.16)';
  return (
    <svg className="dx-kpi__chart" viewBox="0 0 120 34" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 26 C18 24, 26 10, 40 12 C54 14, 60 28, 76 20 C92 12, 104 8, 120 11 L120 34 L0 34 Z" fill={fill} />
      <path d="M0 26 C18 24, 26 10, 40 12 C54 14, 60 28, 76 20 C92 12, 104 8, 120 11" fill="none" stroke={stroke} strokeWidth="2" />
    </svg>
  );
}

function DashboardPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const { data: userData, isLoading: loadingUser } = useGetUserProfileQuery();
  const user = userData?.data.user;

  const { data: balanceData, isLoading: loadingBalance } = useGetBalanceQuery();
  const { data: transactionsData, isLoading: loadingTransactions } = useGetMyTransactionsQuery({
    limit: 5,
    page: 1,
  });

  const { data: proxiesData } = useGetProxiesQuery({ status: 'active', limit: 1 });
  const { data: permanentNumbersData } = useGetPermanentNumbersQuery({ status: 'active', limit: 1 });
  const { data: temporaryNumbersData } = useGetTemporaryNumbersQuery({ status: 'active', limit: 1 });

  const isLoading = loadingUser || loadingBalance || loadingTransactions;

  const totalBalance = useMemo(() => {
    if (!balanceData?.data) return 0;
    return (
      balanceData.data.balance +
      balanceData.data.referralBalance +
      balanceData.data.cashbackBalance
    );
  }, [balanceData]);

  const activeProxies = proxiesData?.data.pagination.total || 0;
  const activePermanentNumbers = permanentNumbersData?.data.pagination.total || 0;
  const activeTemporaryNumbers = temporaryNumbersData?.data.pagination.total || 0;
  const recentTransactions = transactionsData?.data.transactions || [];

  const creditTypes = new Set([
    'recharge',
    'referral_bonus',
    'cashback_claim',
    'cashback_earn',
    'refund',
    'admin_add',
    'admin_add_referral',
    'admin_add_cashback',
  ]);

  const getTransactionTypeLabel = (type: string): string => {
    const typeMap: Record<string, string> = {
      recharge: 'recharge',
      purchase: 'purchase',
      referral_bonus: 'referralBonus',
      refund: 'refund',
      admin_add: 'adminAdd',
      admin_deduct: 'adminDeduct',
      cashback_claim: 'cashbackClaim',
      cashback_earn: 'cashbackEarn',
      admin_add_referral: 'adminAddReferral',
      admin_deduct_referral: 'adminDeductReferral',
      admin_add_cashback: 'adminAddCashback',
      admin_deduct_cashback: 'adminDeductCashback',
    };
    return typeMap[type] ?? type;
  };

  if (isLoading) {
    return (
      <div className="dx-dash">
        <div className="flex justify-center items-center py-16">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="dx-dash">
      <div className="dx-dash__top">
        <div>
          <h1>{t('dashboard.title')}</h1>
          <p>{t('dashboard.subtitle', { name: user?.firstName || t('dashboard.user') })}</p>
        </div>
        <Link to="/recharge" className="dx-btn dx-btn--primary">
          {t('dashboard.quickActions.recharge')}
        </Link>
      </div>

      <div className="dx-kpi-grid">
        <div className="dx-kpi">
          <div className="dx-kpi__label">{t('dashboard.balance.main')}</div>
          <div className="dx-kpi__value">${balanceData?.data.balance.toFixed(2) || '0.00'}</div>
          <MiniSpark />
        </div>
        <div className="dx-kpi dx-kpi--accent">
          <div className="dx-kpi__label">{t('dashboard.balance.total')}</div>
          <div className="dx-kpi__value">${totalBalance.toFixed(2)}</div>
          <MiniSpark light />
        </div>
        <div className="dx-kpi">
          <div className="dx-kpi__label">{t('dashboard.balance.referral')}</div>
          <div className="dx-kpi__value">${balanceData?.data.referralBalance.toFixed(2) || '0.00'}</div>
          <MiniSpark />
        </div>
        <div className="dx-kpi">
          <div className="dx-kpi__label">{t('dashboard.balance.cashback')}</div>
          <div className="dx-kpi__value">${balanceData?.data.cashbackBalance.toFixed(2) || '0.00'}</div>
          <MiniSpark />
        </div>
      </div>

      <div className="dx-dash-grid" style={{ marginBottom: '0.85rem' }}>
        <div className="dx-panel">
          <h2>{t('dashboard.services.title')}</h2>
          <div className="dx-stat-row">
            <Link to="/proxies/list" className="dx-stat">
              <span>{t('dashboard.services.proxies')}</span>
              <strong>{activeProxies}</strong>
            </Link>
            <Link to="/permanent-numbers/list" className="dx-stat">
              <span>{t('dashboard.services.permanentNumbers')}</span>
              <strong>{activePermanentNumbers}</strong>
            </Link>
            <Link to="/temporary-numbers/list" className="dx-stat">
              <span>{t('dashboard.services.temporaryNumbers')}</span>
              <strong>{activeTemporaryNumbers}</strong>
            </Link>
          </div>
        </div>

        <div className="dx-panel">
          <h2>{t('dashboard.quickActions.title')}</h2>
          <div className="dx-actions-grid">
            <Link to="/proxies/purchase" className="dx-action">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8">
                <path d="M12 3 4 7v5c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V7l-8-4z" strokeLinejoin="round" />
              </svg>
              <p>{t('dashboard.quickActions.purchaseProxy')}</p>
            </Link>
            <Link to="/permanent-numbers/purchase" className="dx-action">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8">
                <rect x="6" y="3" width="12" height="18" rx="2.5" />
              </svg>
              <p>{t('dashboard.quickActions.purchasePermanentNumber')}</p>
            </Link>
            <Link to="/temporary-numbers" className="dx-action">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8">
                <path d="M4 6h16v9H8l-4 3V6z" strokeLinejoin="round" />
              </svg>
              <p>{t('dashboard.quickActions.purchaseTemporaryNumber')}</p>
            </Link>
            <a href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer" className="dx-action">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#229ED9">
                <path d="M21.8 4.2 2.9 11.5c-1.3.5-1.3 1.2-.2 1.5l4.8 1.5 1.8 5.6c.2.7.4.9 1 .9.6 0 .8-.3 1.1-.6l2.7-2.6 5.6 4.1c1 .6 1.8.3 2.1-.9L23 5.7c.3-1.3-.5-1.9-1.2-1.5z" />
              </svg>
              <p>{t('dashboard.quickActions.purchaseViaBot')}</p>
            </a>
          </div>
        </div>
      </div>

      <div className="dx-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ margin: 0 }}>
            {t('dashboard.recentTransactions.title', {
              defaultValue: 'Recent transactions',
            })}
          </h2>
          <Link to="/transactions" className="dx-btn dx-btn--ghost" style={{ height: 34, fontSize: 12 }}>
            {t('dashboard.recentTransactions.viewAll', { defaultValue: 'View all' })}
          </Link>
        </div>

        {recentTransactions.length === 0 ? (
          <p style={{ color: 'var(--dx-muted)', margin: 0 }}>{t('dashboard.recentTransactions.empty')}</p>
        ) : (
          recentTransactions.map((transaction) => {
            const isCredit = creditTypes.has(transaction.type);
            return (
              <div key={transaction._id} className="dx-tx">
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--dx-text)' }}>
                    {t(`transactions.types.${getTransactionTypeLabel(transaction.type)}`)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--dx-muted)' }}>
                    {formatDate(transaction.createdAt, 'PP h:mm a', currentLanguage)}
                  </div>
                </div>
                <div style={{ textAlign: 'end' }}>
                  <div
                    style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 700,
                      color: isCredit ? '#4ade80' : '#f87171',
                    }}
                  >
                    {isCredit ? '+' : '-'}${Math.abs(transaction.amount).toFixed(2)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--dx-muted)' }}>
                    {t(`transactions.status.${transaction.status}`)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
